import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useActivateInboundMailbox, useSetTaxId } from "../api/receivedInvoicesApi";

type Props = {
  accountId: string;
  role: AccountRole;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Step = "activating" | "needs_tax_id" | "ready" | "error";

// Onboarding del buzón (spec §10). No existe un GET del estado del buzón: el
// POST de activación es idempotente (si ya está activo devuelve la dirección
// sin rotar el token) y su 409 TAX_ID_REQUIRED es lo que dispara el paso de
// cédula — el flujo se descubre hablando con el backend, no adivinando.
export function MailboxSetupDialog({ accountId, role, open, onOpenChange }: Props) {
  const activate = useActivateInboundMailbox(accountId);
  const setTaxId = useSetTaxId(accountId);

  const [step, setStep] = useState<Step>("activating");
  const [address, setAddress] = useState<string | null>(null);
  const [taxIdInput, setTaxIdInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function tryActivate() {
    setStep("activating");
    setError(null);
    try {
      const result = await activate.mutateAsync();
      setAddress(result.address);
      setStep("ready");
    } catch (e) {
      if (e instanceof ApiError && e.code === "TAX_ID_REQUIRED") {
        setStep("needs_tax_id");
        return;
      }
      setError(e instanceof ApiError ? e.message : "Ocurrió un error inesperado.");
      setStep("error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: el intent es "al abrir" — tryActivate se recrea en cada render y agregarlo re-dispararía el POST en cada re-render.
  useEffect(() => {
    if (!open) return;
    setAddress(null);
    setTaxIdInput("");
    void tryActivate();
  }, [open]);

  async function handleTaxIdSubmit(e: React.FormEvent) {
    e.preventDefault();
    const digits = taxIdInput.replace(/\D/g, "");
    if (!/^\d{9,12}$/.test(digits)) {
      setError("La cédula debe tener entre 9 y 12 dígitos, solo números.");
      return;
    }
    setError(null);
    try {
      await setTaxId.mutateAsync(digits);
      await tryActivate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar la cédula.");
    }
  }

  async function handleCopy() {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      toast.success("Dirección copiada.");
    } catch {
      toast.error("No se pudo copiar — seleccioná la dirección y copiala a mano.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Buzón de facturas</DialogTitle>
          <DialogDescription>
            Las facturas que lleguen a la dirección del buzón aparecen solas en Recibidos.
          </DialogDescription>
        </DialogHeader>

        {step === "activating" && (
          <output className="block py-6 text-center text-[13px] text-app-ink-3">
            Preparando el buzón…
          </output>
        )}

        {step === "needs_tax_id" && (
          <TaxIdStep
            role={role}
            value={taxIdInput}
            onChange={setTaxIdInput}
            onSubmit={handleTaxIdSubmit}
            pending={setTaxId.isPending || activate.isPending}
            error={error}
          />
        )}

        {step === "ready" && address && <ReadyStep address={address} onCopy={handleCopy} />}

        {step === "error" && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p role="alert" className="text-[13px] text-rose-700">
              {error ?? "No se pudo preparar el buzón."}
            </p>
            <button
              type="button"
              onClick={() => void tryActivate()}
              className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
            >
              Reintentar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaxIdStep({
  role,
  value,
  onChange,
  onSubmit,
  pending,
  error,
}: {
  role: AccountRole;
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  pending: boolean;
  error: string | null;
}) {
  if (role !== "owner") {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
        Falta la cédula de la finca y solo el propietario de la cuenta puede registrarla. Pedile que
        entre a Recibidos y configure el buzón.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="mailbox-tax-id" className="font-medium text-[13px] text-app-ink-2">
          Cédula de la finca
        </label>
        <input
          id="mailbox-tax-id"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={12}
          disabled={pending}
          placeholder="Física (9 dígitos) o jurídica (10)"
          className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
        />
        <span className="text-[12px] text-app-ink-3">
          <strong className="font-medium text-app-ink-2">Importante:</strong> usá la misma cédula
          con la que hacés tus compras — la que tus proveedores ponen en la factura. Si una factura
          llega a nombre de otra cédula, no se procesa: queda en cuarentena sin asignarse.
        </span>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={pending || value.trim() === ""}
        className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
      >
        {pending ? "Guardando…" : "Guardar y activar buzón"}
      </button>
    </form>
  );
}

function ReadyStep({ address, onCopy }: { address: string; onCopy: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <span className="font-medium text-[13px] text-app-ink-2">Dirección del buzón</span>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-lg border border-app-border bg-app-surface-2 px-3 py-2 font-mono text-[13px] text-app-ink-1">
            {address}
          </code>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-app-border bg-app-surface px-3 font-medium text-[13px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
          >
            <Copy size={14} aria-hidden="true" />
            Copiar
          </button>
        </div>
      </div>

      <section aria-label="Instrucciones de reenvío" className="flex flex-col gap-3">
        <div>
          <h3 className="font-medium text-[13px] text-app-ink-1">Reenvío automático desde Gmail</h3>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-[13px] text-app-ink-2">
            <li>En Gmail: Configuración → Ver todos los ajustes → Reenvío y correo POP/IMAP.</li>
            <li>Agregar una dirección de reenvío → pegá la dirección de arriba.</li>
            <li>
              <strong className="font-medium text-app-ink-1">
                Gmail le va a pedir un código de verificación — ese correo aparecerá aquí mismo en
                Recibidos.
              </strong>
            </li>
            <li>Elegí "Reenviar una copia del correo entrante" y guardá los cambios.</li>
          </ol>
        </div>

        <div>
          <h3 className="font-medium text-[13px] text-app-ink-1">Desde Outlook</h3>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-[13px] text-app-ink-2">
            <li>Configuración → Correo → Reenvío.</li>
            <li>Activá el reenvío, pegá la dirección y marcá "Conservar una copia".</li>
          </ol>
        </div>

        <p className="rounded-lg bg-app-surface-2 px-3 py-2 text-[12px] text-app-ink-3">
          También podés reenviar facturas puntuales a mano a esa misma dirección.
        </p>
      </section>
    </div>
  );
}
